/**
 * Sold as / Bought as values for a variant, using the same matrix as
 * Variant Mapping. Column keys are `{type}:{occurrence}` from
 * buildVariantManagementColumns. The string at a key is
 * variantManagementCellValue for that column.
 */
import {
  inheritedMappingRowVisible,
  observationTimestamp,
  type CloneMappingReadFilter,
} from './cloneMappingCutoff.ts';
import { toMatchKey } from './matchKey.ts';
import type { VariantMappingRow } from '../types/variantMapping.ts';
import {
  buildVariantManagementColumns,
  buildVariantManagementRows,
  variantManagementCellValue,
} from './variantManagementColumns.ts';

type CatalogEntry = {
  master_product_id: string;
  eve_id?: number;
  linked_identifiers?: readonly { type: string; value: string; linkId?: string }[];
};

export type DesignationMasterProduct = {
  id: string;
  merged_into_id?: string | null;
};

export type DesignationIdentifierLink = {
  id?: string | null;
  master_product_id?: string | null;
  identifier_type?: string | null;
  identifier_value?: string | null;
  active?: boolean | null;
  created_at?: string | null;
};

export type DesignationIdentifierObservation = {
  duplicate_of?: string | null;
  interpreted_identifier_type?: string | null;
  raw_source_label?: string | null;
  raw_as_observed_value?: string | null;
  asserted_at?: string | null;
  created_at?: string | null;
};

export type DesignationMatrixOptions = {
  masters?: ReadonlyArray<DesignationMasterProduct>;
  cloneReadFilter?: CloneMappingReadFilter | null;
};

export function designationValuesByMaster(
  catalog: ReadonlyArray<CatalogEntry>,
  mappingRows: readonly VariantMappingRow[],
  options?: { identifiersUnavailable?: boolean },
): Map<string, Record<string, string | null>> {
  const rows = buildVariantManagementRows(catalog, mappingRows, options);
  const columns = buildVariantManagementColumns(rows);
  const byMaster = new Map<string, Record<string, string | null>>();
  for (const row of rows) {
    const values: Record<string, string | null> = {};
    for (const column of columns) {
      const value = variantManagementCellValue(row, column);
      if (value) values[column.key] = value;
    }
    byMaster.set(row.variantId, values);
  }
  return byMaster;
}

function stubMappingRow(input: {
  identifierType: string;
  externalIdentifier: string;
  masterProductId: string;
}): VariantMappingRow {
  return {
    observationId: '',
    organisationId: '',
    externalIdentifier: input.externalIdentifier,
    productName: '',
    identifierType: input.identifierType,
    sourceChannel: '',
    sourceDetail: null,
    ingestionTransport: '',
    status: 'matched',
    matchedVariant: {
      master_product_id: input.masterProductId,
      display_name: '',
      code: '',
      matched_at: null,
      resolutionSource: 'manual',
    },
    suggestionId: null,
    suggestedCandidates: [],
  };
}

/**
 * Rebuild the Variant Mapping matrix from the same tables the screen reads.
 * Observations that match an active link come first (input order), then
 * remaining active links in input order. Merged masters are omitted when a
 * master list is provided. Pass links in id order.
 */
export function designationValuesFromIdentifierSources(input: {
  links: ReadonlyArray<DesignationIdentifierLink>;
  observations?: ReadonlyArray<DesignationIdentifierObservation>;
  masters?: ReadonlyArray<DesignationMasterProduct>;
  cloneReadFilter?: CloneMappingReadFilter | null;
}): Map<string, Record<string, string | null>> {
  const visible = (timestamp: string | null | undefined) =>
    !input.cloneReadFilter ||
    inheritedMappingRowVisible(timestamp, input.cloneReadFilter.orgCreatedAt, input.cloneReadFilter.cutoffDate);

  const activeLinks = input.links.filter((link) => {
    if (!link.active || !link.master_product_id?.trim()) return false;
    return visible(link.created_at);
  });

  const catalogMasterIds = input.masters
    ? input.masters.filter((master) => master.id && !master.merged_into_id).map((master) => master.id)
    : [...new Set(activeLinks.map((link) => link.master_product_id!.trim()))];
  const catalogIds = new Set(catalogMasterIds);

  const linkedByMaster = new Map<string, { type: string; value: string; linkId?: string }[]>();
  for (const id of catalogMasterIds) linkedByMaster.set(id, []);
  for (const link of activeLinks) {
    const masterId = link.master_product_id!.trim();
    const list = linkedByMaster.get(masterId);
    if (!list) continue;
    const type = link.identifier_type?.trim() ?? '';
    const value = link.identifier_value?.trim() ?? '';
    if (!type || !value) continue;
    list.push({ type, value, ...(link.id ? { linkId: link.id } : {}) });
  }

  const linksForMatch = [...activeLinks].sort((left, right) => {
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return leftTime - rightTime;
  });
  const linkByKey = new Map<string, DesignationIdentifierLink>();
  for (const link of linksForMatch) {
    const type = link.identifier_type?.trim().toLowerCase() ?? '';
    const value = link.identifier_value ?? '';
    if (!type || !value.trim()) continue;
    linkByKey.set(`${type}:${toMatchKey(value)}`, link);
  }

  const mappingRows: VariantMappingRow[] = [];
  for (const observation of input.observations ?? []) {
    if (observation.duplicate_of) continue;
    if (!visible(observationTimestamp(observation))) continue;
    const type = observation.interpreted_identifier_type ?? observation.raw_source_label ?? '';
    const rawValue = observation.raw_as_observed_value ?? '';
    if (!type.trim() || !rawValue.trim()) continue;
    const link = linkByKey.get(`${type.trim().toLowerCase()}:${toMatchKey(rawValue)}`);
    const masterId = link?.master_product_id?.trim() ?? '';
    if (!masterId || !catalogIds.has(masterId)) continue;
    mappingRows.push(stubMappingRow({
      identifierType: type,
      externalIdentifier: rawValue,
      masterProductId: masterId,
    }));
  }

  return designationValuesByMaster(
    catalogMasterIds.map((id) => ({
      master_product_id: id,
      linked_identifiers: linkedByMaster.get(id) ?? [],
    })),
    mappingRows,
  );
}
